import React, { useRef, useState, useCallback } from 'react';
import {
  Send,
  Image as ImageIcon,
  Video,
  X,
  FileVideo,
  FileImage,
} from 'lucide-react';
import { toast } from 'react-toastify';

const MAX_VIDEO_SIZE_MB = 50;
const MAX_VIDEO_SIZE_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;

const iconBtn = {
  width: 32,
  height: 32,
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  flexShrink: 0,
  border: 'none',
  cursor: 'pointer',
  transition: 'background 0.15s',
};

const ChatInput = ({
  onSendMessage,
  onSendImage,
  onSendVideo,
  disabled,
  placeholder,
  uploadProgress,
  isUploading,
}) => {
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState(null); // { file, type: 'image'|'video', previewUrl }
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const textareaRef = useRef(null);

  /* ── auto-resize textarea ── */
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }, []);

  const clearAttachment = () => {
    if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
    setAttachment(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const handleSend = async () => {
    if (disabled || isUploading) return;
    const hasText = !!message.trim();
    const hasAttachment = !!attachment;
    if (!hasText && !hasAttachment) return;

    try {
      if (hasText) {
        await onSendMessage?.(message);
        setMessage('');
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
      }
      if (hasAttachment) {
        if (attachment.type === 'image') {
          await onSendImage?.(attachment.file);
        } else {
          await onSendVideo?.(attachment.file);
        }
        clearAttachment();
      }
    } catch (e) {
      // Errors handled upstream via toast
    }
  };

  /* Enter → send | Shift+Enter → new line */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    // Shift+Enter: browser inserts \n naturally into textarea — no extra handling needed
  };

  const handleTextChange = (e) => {
    setMessage(e.target.value);
    autoResize();
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    clearAttachment();
    setAttachment({
      file,
      type: 'image',
      previewUrl: URL.createObjectURL(file),
    });
    e.target.value = '';
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      toast.error(`Video too large — maximum is ${MAX_VIDEO_SIZE_MB} MB.`);
      e.target.value = '';
      return;
    }
    clearAttachment();
    setAttachment({
      file,
      type: 'video',
      previewUrl: URL.createObjectURL(file),
    });
    e.target.value = '';
  };

  const canSend =
    !disabled && !isUploading && (!!message.trim() || !!attachment);

  return (
    <div style={{ width: '100%' }}>
      {/* ── Attachment preview ── */}
      {attachment && (
        <div
          className='mb-2 p-2 rounded-3 d-flex align-items-center gap-2'
          style={{
            background: '#f1f3f9',
            border: '1px dashed #c5cae9',
            fontSize: '0.82rem',
          }}
        >
          {attachment.type === 'image' ? (
            <img
              src={attachment.previewUrl}
              alt='preview'
              style={{
                width: 44,
                height: 44,
                objectFit: 'cover',
                borderRadius: 6,
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              className='d-flex align-items-center justify-content-center rounded'
              style={{
                width: 44,
                height: 44,
                background: '#212529',
                flexShrink: 0,
              }}
            >
              <FileVideo size={20} color='#fff' />
            </div>
          )}
          <div className='flex-grow-1 overflow-hidden'>
            <div
              className='fw-semibold text-truncate'
              style={{ maxWidth: '100%' }}
            >
              {attachment.file.name}
            </div>
            <small className='text-muted'>
              {attachment.type === 'video' ? '🎬 Video' : '🖼️ Image'}{' '}
              &nbsp;·&nbsp;
              {(attachment.file.size / 1024 / 1024).toFixed(1)} MB
            </small>
          </div>
          <button
            type='button'
            className='btn btn-sm btn-outline-danger'
            onClick={clearAttachment}
            disabled={isUploading}
            title='Remove'
            style={{ borderRadius: 6, padding: '2px 6px' }}
          >
            <X size={13} />
          </button>
        </div>
      )}

      {/* ── Upload progress ── */}
      {isUploading && (
        <div className='mb-2'>
          <div className='d-flex justify-content-between align-items-center mb-1'>
            <small className='text-muted fw-semibold d-flex align-items-center gap-1'>
              {attachment?.type === 'video' ? (
                <>
                  <FileVideo size={12} /> Uploading video… large files may take
                  a moment
                </>
              ) : (
                <>
                  <FileImage size={12} /> Uploading image…
                </>
              )}
            </small>
            <small className='fw-bold' style={{ color: '#667eea' }}>
              {uploadProgress}%
            </small>
          </div>
          <div className='progress' style={{ height: 5, borderRadius: 4 }}>
            <div
              className='progress-bar progress-bar-striped progress-bar-animated'
              role='progressbar'
              style={{
                width: `${uploadProgress}%`,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            />
          </div>
        </div>
      )}

      {/* ── Main input box ── */}
      <div
        className='d-flex align-items-center gap-0'
        style={{
          border: '1.5px solid #dee2e6',
          borderRadius: 12,
          background: disabled ? '#f8f9fa' : '#fff',
          boxShadow: '0 1px 4px rgba(102,126,234,0.08)',
        }}
      >
        {/* Hidden file inputs */}
        <input
          ref={imageInputRef}
          type='file'
          accept='image/*'
          onChange={handleImageChange}
          style={{ display: 'none' }}
          disabled={disabled}
        />
        <input
          ref={videoInputRef}
          type='file'
          accept='.mp4,.webm,.mov,.avi,.mkv,.flv,.wmv,.m4v,.3gp,.mpeg,.mpg,video/*'
          onChange={handleVideoChange}
          style={{ display: 'none' }}
          disabled={disabled}
        />

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          rows={1}
          className='form-control border-0 shadow-none'
          placeholder={
            disabled
              ? placeholder || 'This chat is closed'
              : placeholder || 'Type your message…'
          }
          value={message}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          disabled={disabled || isUploading}
          style={{
            resize: 'none',
            minHeight: 42,
            maxHeight: 140,
            borderRadius: 12,
            background: 'transparent',
            overflowY: 'auto',
            lineHeight: '1.5',
            padding: '10px 12px',
            flex: 1,
            boxShadow: 'none',
            outline: 'none',
          }}
        />

        {/* Send button */}
        <div
          className='d-flex align-items-center'
          style={{ padding: '0 8px 0 4px' }}
        >
          <button
            type='button'
            onClick={handleSend}
            disabled={!canSend}
            title='Send (Enter)'
            style={{
              ...iconBtn,
              width: 38,
              height: 38,
              borderRadius: 10,
              background: canSend
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : '#e9ecef',
              color: canSend ? '#fff' : '#adb5bd',
            }}
          >
            {isUploading ? (
              <span
                className='spinner-border'
                style={{ width: 16, height: 16, borderWidth: 2 }}
                role='status'
              />
            ) : (
              <Send size={16} />
            )}
          </button>
        </div>
      </div>

      {/* ── Upload buttons row ── */}
      {!disabled && (
        <div className='d-flex align-items-center gap-2 mt-2'>
          {/* Image upload button */}
          <button
            type='button'
            onClick={() => !isUploading && imageInputRef.current?.click()}
            disabled={isUploading}
            title='Attach image'
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 12px',
              border: '1.5px dashed #667eea',
              borderRadius: 8,
              background: '#f0f1ff',
              color: '#667eea',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: isUploading ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
              whiteSpace: 'nowrap',
            }}
          >
            <ImageIcon size={15} />
            Upload Image
          </button>

          {/* Video upload button */}
          <button
            type='button'
            onClick={() => !isUploading && videoInputRef.current?.click()}
            disabled={isUploading}
            title={`Attach video (max ${MAX_VIDEO_SIZE_MB} MB) — mp4, webm, mov, avi, mkv, flv, wmv, m4v, 3gp, mpeg`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 12px',
              border: '1.5px dashed #20c997',
              borderRadius: 8,
              background: '#eafaf5',
              color: '#12a87a',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: isUploading ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
              whiteSpace: 'nowrap',
            }}
          >
            <Video size={15} />
            Upload Video
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatInput;
